import { useState } from "react";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import {
  FileText, Users, BookOpen, MessageSquareMore,
  ChevronDown, ChevronUp, Search, Calendar, Video, MapPin,
} from "lucide-react";

type Note = { id: string; authorId: string; content: string; updatedAt: string };
type SessionEntry = {
  sessionId: string;
  sessionStatus: string;
  proposedStart: string;
  proposedEnd: string;
  mode: string;
  trainerId: string;
  trainerFirstName: string;
  trainerLastName: string;
  studentId: string;
  studentFirstName: string;
  studentLastName: string;
  notes: Note[];
  noteCount: number;
  lastNoteAt: string;
};
type NotesData = {
  totalSessions: number;
  totalNotes: number;
  sessionsWithBothNotes: number;
  topTrainers: { name: string; noteCount: number }[];
  sessions: SessionEntry[];
};

const STATUS_COLOR: Record<string, string> = {
  COMPLETED: "text-emerald-400",
  CONFIRMED: "text-blue-400",
  PENDING: "text-amber-400",
  CANCELLED: "text-red-400",
  REJECTED: "text-red-400",
};

export default function AdminSessionNotes() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery<NotesData>({
    queryKey: ["admin-session-notes", user?.gymId],
    queryFn: () => apiFetch("/admin/session-notes"),
    enabled: !!user?.gymId,
  });

  const filtered = (data?.sessions ?? []).filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      `${s.trainerFirstName} ${s.trainerLastName}`.toLowerCase().includes(q) ||
      `${s.studentFirstName} ${s.studentLastName}`.toLowerCase().includes(q) ||
      s.notes.some(n => n.content.toLowerCase().includes(q))
    );
  });

  function toggleExpand(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Session Notes</h1>
          <p className="text-muted-foreground text-lg">Training activity captured by coaches and students across your gym.</p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><BookOpen className="w-14 h-14 text-primary" /></div>
            <CardHeader className="pb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sessions with Notes</p>
              <p className="text-3xl font-mono text-white mt-1">
                {isLoading ? "…" : (data?.totalSessions ?? 0)}
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{isLoading ? "" : `${data?.totalNotes ?? 0} notes total`}</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><MessageSquareMore className="w-14 h-14 text-primary" /></div>
            <CardHeader className="pb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Both Sides Noted</p>
              <p className="text-3xl font-mono text-white mt-1">
                {isLoading ? "…" : (data?.sessionsWithBothNotes ?? 0)}
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Trainer + student both wrote notes</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><Users className="w-14 h-14 text-primary" /></div>
            <CardHeader className="pb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Most Active Coach</p>
              <p className="text-3xl font-mono text-white mt-1 truncate">
                {isLoading ? "…" : (data?.topTrainers?.[0]?.name ?? "—")}
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {isLoading ? "" : data?.topTrainers?.[0] ? `${data.topTrainers[0].noteCount} note${data.topTrainers[0].noteCount !== 1 ? "s" : ""}` : "No notes yet"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Search + list */}
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by trainer, student, or note content…"
              className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-white font-medium text-lg">
                {search ? "No matching notes" : "No session notes yet"}
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                {search ? "Try a different search term." : "Notes will appear here once trainers or students start writing them."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(session => {
                const isOpen = expanded.has(session.sessionId);
                return (
                  <Card key={session.sessionId} className="glass-panel border-white/5 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleExpand(session.sessionId)}
                      className="w-full text-left p-5 hover:bg-white/3 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                            {session.trainerFirstName?.[0] ?? "T"}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-white text-sm">
                                {session.trainerFirstName} {session.trainerLastName}
                              </span>
                              <span className="text-muted-foreground text-sm">→</span>
                              <span className="text-white/70 text-sm">
                                {session.studentFirstName} {session.studentLastName}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(parseISO(session.proposedStart), "MMM d, yyyy")}
                              </span>
                              <span className="flex items-center gap-1">
                                {session.mode === "VIDEO"
                                  ? <Video className="w-3 h-3" />
                                  : <MapPin className="w-3 h-3" />}
                                {session.mode === "VIDEO" ? "Video" : "In Person"}
                              </span>
                              <span className={cn("font-semibold uppercase tracking-wider text-[10px]", STATUS_COLOR[session.sessionStatus] ?? "text-muted-foreground")}>
                                {session.sessionStatus}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <FileText className="w-3.5 h-3.5 text-primary" />
                            <span className="font-medium text-primary">{session.noteCount}</span>
                            <span>note{session.noteCount !== 1 ? "s" : ""}</span>
                          </div>
                          <span className="text-xs text-muted-foreground hidden sm:block">
                            {format(parseISO(session.lastNoteAt), "MMM d")}
                          </span>
                          {isOpen
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                        {session.notes.map(note => {
                          const isTrainer = note.authorId === session.trainerId;
                          return (
                            <div key={note.id} className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
                                  isTrainer ? "bg-primary/20 text-primary" : "bg-blue-500/20 text-blue-400"
                                )}>
                                  {isTrainer ? session.trainerFirstName?.[0] : session.studentFirstName?.[0]}
                                </div>
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                  {isTrainer
                                    ? `${session.trainerFirstName} ${session.trainerLastName} (Trainer)`
                                    : `${session.studentFirstName} ${session.studentLastName} (Student)`}
                                </span>
                                <span className="text-xs text-white/30 ml-auto">
                                  {format(parseISO(note.updatedAt), "MMM d, h:mm a")}
                                </span>
                              </div>
                              <div className={cn(
                                "p-3 rounded-lg text-sm text-white/80 whitespace-pre-wrap leading-relaxed border",
                                isTrainer ? "bg-primary/5 border-primary/10" : "bg-blue-500/5 border-blue-500/10"
                              )}>
                                {note.content}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
